import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import { useTranslation } from '../i18n';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { enUS, ptBR, es } from 'date-fns/locale';

const Calendar = () => {
  const { t, language } = useTranslation();
  
  // Get locale for date-fns
  const getLocale = () => {
    switch (language) {
      case 'pt':
        return ptBR;
      case 'es':
        return es;
      default:
        return enUS;
    }
  };
  
  // Get localized day names
  const getDayNames = () => {
    if (language === 'pt') {
      return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    }
    if (language === 'es') {
      return ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    }
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  };
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  }, [currentDate]); // Refetch when month changes

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const monthStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      const response = await apiClient.get(`/calendar?status=active&start_date=${monthStart}&end_date=${monthEnd}&limit=200`);
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load calendar events', t));
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEventsForDate = (date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return (Array.isArray(events) ? events : []).filter(event => event.date === dateString);
  };

  const getDaysInMonth = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container" style={{ paddingBottom: '5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="text-lg" style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
            {t('calendar')}
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>
            {t('upcomingEvents')}
          </p>
        </div>

        {error && (
          <div className="error">{error}</div>
        )}

        {/* Calendar Navigation */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button
              onClick={() => navigateMonth('prev')}
              className="btn btn-outline"
              style={{ padding: '0.5rem' }}
            >
              <ChevronLeft size={20} />
            </button>
            
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              {format(currentDate, 'MMMM yyyy', { locale: getLocale() })}
            </h2>
            
            <button
              onClick={() => navigateMonth('next')}
              className="btn btn-outline"
              style={{ padding: '0.5rem' }}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: '#e0e0e0', borderRadius: '0.5rem', overflow: 'hidden' }}>
            {/* Day headers */}
            {getDayNames().map(day => (
              <div
                key={day}
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#666'
                }}
              >
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {getDaysInMonth().map(day => {
              const dayEvents = getEventsForDate(day);
              const isSelected = isSameDay(day, selectedDate);
              const isCurrentDay = isToday(day);
              const isInCurrentMonth = isCurrentMonth(day);
              
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  style={{
                    backgroundColor: isSelected ? '#007bff' : 'white',
                    color: isSelected ? 'white' : isCurrentDay ? '#007bff' : isInCurrentMonth ? '#333' : '#ccc',
                    border: 'none',
                    padding: '0.75rem 0.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: '3rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: isCurrentDay ? '600' : '400',
                    opacity: isInCurrentMonth ? 1 : 0.4
                  }}
                >
                  {format(day, 'd')}
                  {dayEvents.length > 0 && (
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? 'white' : '#007bff',
                        marginTop: '2px'
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Events */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {t('eventsFor')} {format(selectedDate, 'MMMM d, yyyy', { locale: getLocale() })}
            </h2>
          </div>
          
          {selectedDateEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <CalendarIcon size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>{t('noEvents')}</p>
              <p style={{ fontSize: '0.875rem' }}>
                {t('addEventsBot')}
              </p>
            </div>
          ) : (
            <ul className="list">
              {selectedDateEvents
                .sort((a, b) => {
                  // Sort by start_time, putting events without time at the end
                  if (!a.start_time && !b.start_time) return 0;
                  if (!a.start_time) return 1;
                  if (!b.start_time) return -1;
                  return a.start_time.localeCompare(b.start_time);
                })
                .map((event) => (
                <li key={event.event_id} className="list-item">
                  <div className="list-item-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div className="list-item-title">{event.title}</div>
                      {event.platform && (
                        <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                          {event.platform === 'telegram' ? '📱' : event.platform === 'whatsapp' ? '💬' : event.platform}
                        </span>
                      )}
                      {event.integration_name === 'google_calendar' && (
                        <span className="badge" style={{ 
                          fontSize: '0.7rem', 
                          backgroundColor: '#e8f5e9', 
                          color: '#2e7d32',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }} title="Synced with Google Calendar">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Google
                        </span>
                      )}
                    </div>
                    
                    {event.description && (
                      <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                        {event.description}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem', color: '#007bff', fontWeight: '500' }}>
                      {event.start_time && (
                        <span>🕐 {event.start_time}</span>
                      )}
                      {event.end_time && event.start_time && (
                        <span>→</span>
                      )}
                      {event.end_time && (
                        <span>🕐 {event.end_time}</span>
                      )}
                      {!event.start_time && !event.end_time && (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>📅 {t('allDay')}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Monthly Summary */}
        {events.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">{t('thisMonth')}</h2>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">
                  {events.length}
                </div>
                <div className="stat-label">{t('totalEvents')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">
                  {getDaysInMonth().filter(day => getEventsForDate(day).length > 0).length}
                </div>
                <div className="stat-label">{t('daysWithEvents')}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Calendar;